import { isNil } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { KCL_DOCUMENT_CHECK_ADMINS, KCL_DOCUMENT_CHECK_MARPO } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { clearFetchStore } from 'features/common/fetch/actions';
import { urls } from 'features/common/fetch/constants';
import { DOCUMENTS_TO_REVIEW_FORM_NAME } from 'features/views/productionControl/documentsToReview/form';
import { fetchMyDocuments, reload as reloadMyDocuments } from 'features/views/productionControl/myDocuments/actions';
import { MY_DOCUMENTS_FORM_NAME } from 'features/views/productionControl/myDocuments/form';
import {
	FIELDS as SEL_CRITERIA_FIELD,
	SELECTION_CRITERIA_FORM_NAME,
} from 'features/views/productionControl/selectionCriteria/form';
import { setDetailViewed } from 'features/views/productionControlDetail/slice';
import { getUserOid } from 'features/views/user/selectors';

import {
	selectionCriteriaBEFilters,
	selectionCriteriaBEFiltersMap,
	tableBEFilters,
	tableBEFiltersMap,
} from '../constants';
import { fetchDocumentsToReview, reload as reloadDocumentsToReview } from '../documentsToReview/actions';
import { getDocumentsToReview, getMyDocuments, getUserIsSuperior, getWaves } from '../selectors';
import {
	resetClientsDocumentsToReview,
	resetDocumentsToReview,
	resetRegions,
	resetSubordinates,
	resetSuperiors,
	setRegions,
	setSubordinates,
	setSuperiors,
	setUserIsSuperior,
	setWavesCodelist,
} from '../slice';
import { getFilledValuesForFiltersFromQueryParams } from '../utils';

export const startupSelectionCriteria =
	(createdDateLatestWave) =>
	async (dispatch, getState, { sendControlNext }) => {
		const userIsCentralOrMarpo = hasPermissionByIds(getState())([
			...KCL_DOCUMENT_CHECK_ADMINS,
			...KCL_DOCUMENT_CHECK_MARPO,
		]);

		sendControlNext(
			SELECTION_CRITERIA_FORM_NAME,
			setValue({ fieldName: SEL_CRITERIA_FIELD.wave, value: createdDateLatestWave?.id }),
		);

		if (userIsCentralOrMarpo) {
			dispatch(fetchRegions());
		} else {
			dispatch(fetchSubordinates());
		}
	};

export const resetProductionControl =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const userIsCentralOrMarpo = hasPermissionByIds(getState())([
			...KCL_DOCUMENT_CHECK_ADMINS,
			...KCL_DOCUMENT_CHECK_MARPO,
		]);
		const userIsSuperior = getUserIsSuperior(getState());
		const documentCheckWaveCodelist = getWaves(getState());

		sendControlNext(SELECTION_CRITERIA_FORM_NAME, resetForm());

		sendControlNext(MY_DOCUMENTS_FORM_NAME, resetForm());
		sendControlNext(DOCUMENTS_TO_REVIEW_FORM_NAME, resetForm());

		dispatch(resetRegions());
		dispatch(resetSuperiors());
		dispatch(resetSubordinates());

		dispatch(resetDocumentsToReview());
		dispatch(resetClientsDocumentsToReview());
		dispatch(clearFetchStore(urls.koprDocumentsToReview));

		if (!isNilOrEmpty(documentCheckWaveCodelist)) {
			sendControlNext(
				SELECTION_CRITERIA_FORM_NAME,
				setValue({ fieldName: SEL_CRITERIA_FIELD.wave, value: documentCheckWaveCodelist[0]?.id }),
			);

			if (userIsCentralOrMarpo) {
				dispatch(fetchRegions());
			}

			if (userIsSuperior) {
				dispatch(fetchSubordinates());
			}
		}
	};

export const fetchRegions =
	() =>
	async (dispatch, getState, { sendControlNext, getFormData }) => {
		const isCentral = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_ADMINS);
		const isMarpo = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_MARPO);
		const selectionCriteriaFormData = await getFormData(SELECTION_CRITERIA_FORM_NAME);

		if (!isNilOrEmpty(selectionCriteriaFormData?.wave)) {
			dispatch(
				fetch.actions.get(fetch.constants.urls.koprRegion, {
					onSuccess: [
						(response) => () => {
							if (!response || !response.body) return;
							const data = response?.body;

							if (isCentral) {
								dispatch(setRegions(data?.values));
							}

							if (isMarpo) {
								const values = data?.values;
								dispatch(setRegions(values));
								const firstRegion = values[0];

								sendControlNext(
									SELECTION_CRITERIA_FORM_NAME,
									setValue({ fieldName: SEL_CRITERIA_FIELD.region, value: firstRegion?.id }),
								);
								dispatch(fetchSuperiors());
							}
						},
					],
				}),
			);
		}
	};

export const fetchSuperiors =
	() =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const isMarpo = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_MARPO);
		const selectionCriteriaFormData = await getFormData(SELECTION_CRITERIA_FORM_NAME);
		const userOid = getUserOid(getState());

		if (!isNilOrEmpty(selectionCriteriaFormData?.region)) {
			dispatch(
				fetch.actions.get(fetch.constants.urls.koprSuperiors, {
					queryParams: {
						waveId: selectionCriteriaFormData?.wave,
						regionId: selectionCriteriaFormData?.region,
					},
					onSuccess: [
						(response) => () => {
							if (!response || !response.body) return;
							const data = response?.body;
							const values = data?.values;

							dispatch(setSuperiors(values));

							if (isMarpo) {
								const userAsSuperior = values.find(
									(subordinate) => Number(subordinate?.id) === userOid,
								);

								if (userAsSuperior) {
									sendControlNext(
										SELECTION_CRITERIA_FORM_NAME,
										setValue({ fieldName: SEL_CRITERIA_FIELD.superior, value: userAsSuperior?.id }),
									);
									dispatch(fetchSubordinates());
								}
							}
						},
					],
				}),
			);
		}
	};

export const fetchSubordinates =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const isCentral = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_ADMINS);
		const isMarpo = hasPermissionByIds(getState())(KCL_DOCUMENT_CHECK_MARPO);
		const userIsCentralOrMarpo = isCentral || isMarpo;
		const userOid = getUserOid(getState());

		const selectionCriteriaFormData = await getFormData(SELECTION_CRITERIA_FORM_NAME);

		if (
			(userIsCentralOrMarpo && !isNilOrEmpty(selectionCriteriaFormData?.superior)) ||
			(!userIsCentralOrMarpo && !isNilOrEmpty(selectionCriteriaFormData?.wave))
		) {
			dispatch(
				fetch.actions.get(fetch.constants.urls.koprSubordinates, {
					queryParams: {
						waveId: selectionCriteriaFormData?.wave,
						superiorOid: userIsCentralOrMarpo ? selectionCriteriaFormData?.superior : userOid,
					},
					onSuccess: [
						(response) => () => {
							if (!response || !response.body) return;
							const data = response?.body;
							const values = data?.values;

							const userIsSuperior = !isNilOrEmpty(values);
							dispatch(setUserIsSuperior(userIsSuperior));
							dispatch(setSubordinates(values));

							if (!userIsCentralOrMarpo) {
								dispatch(fetchMyDocuments());
							}

							if (!userIsCentralOrMarpo && userIsSuperior) {
								dispatch(fetchDocumentsToReview());
							}
						},
					],
				}),
			);
		}
	};

export const setOverviewByLastRequest =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const myDocuments = getMyDocuments(getState());
		const documentsToReview = getDocumentsToReview(getState());

		// resetting the detailviewed value, which triggers this thunk
		dispatch(setDetailViewed(false));

		// documentsToReview have been previously fetched / section has been shown > so its not null
		if (!isNil(documentsToReview)) {
			const { queryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.koprDocumentsToReview)(
				getState(),
			);

			// documentsToReview tableFilters - retrieving and setting previous values
			const filledTableFilters = getFilledValuesForFiltersFromQueryParams(
				queryParams,
				tableBEFilters,
				tableBEFiltersMap,
			);
			filledTableFilters.forEach((filter) =>
				sendControlNext(
					DOCUMENTS_TO_REVIEW_FORM_NAME,
					setValue({ fieldName: filter.key, value: filter.value }),
				),
			);

			// selection criteria - retrieving and setting previous values
			const filledSelectionCriteriaFilters = getFilledValuesForFiltersFromQueryParams(
				queryParams,
				selectionCriteriaBEFilters,
				selectionCriteriaBEFiltersMap,
			);

			filledSelectionCriteriaFilters.forEach((filter) =>
				sendControlNext(SELECTION_CRITERIA_FORM_NAME, setValue({ fieldName: filter.key, value: filter.value })),
			);

			// refetch
			dispatch(reloadDocumentsToReview());
		}

		// myDocuments have been previously fetched / section has been shown > so its not null
		if (!isNil(myDocuments)) {
			const { queryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.koprMyDocuments)(getState());

			// myDocuments tableFilters - retrieving and setting previous values
			const filledTableFilters = getFilledValuesForFiltersFromQueryParams(
				queryParams,
				tableBEFilters,
				tableBEFiltersMap,
			);
			filledTableFilters.forEach((filter) =>
				sendControlNext(MY_DOCUMENTS_FORM_NAME, setValue({ fieldName: filter.key, value: filter.value })),
			);

			// selection criteria value - setting previous values
			const filledSelectionCriteriaFilters = getFilledValuesForFiltersFromQueryParams(
				queryParams,
				selectionCriteriaBEFilters,
				selectionCriteriaBEFiltersMap,
			);
			filledSelectionCriteriaFilters.forEach((filter) =>
				sendControlNext(SELECTION_CRITERIA_FORM_NAME, setValue({ fieldName: filter.key, value: filter.value })),
			);

			// refetch
			dispatch(reloadMyDocuments());
		}
	};

export const fetchDocumentCheckWaveCodelist = () => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.koprDocumentsCheckWaves, {
			onSuccess: [
				(response) => () => {
					if (!response || !response.body) return;
					const data = response?.body;
					dispatch(setWavesCodelist(data?.values));
				},
			],
		}),
	);
};

export const initProductionControl = () => (dispatch) => {
	dispatch(resetProductionControl());
	dispatch(fetchDocumentCheckWaveCodelist());
};
